import { useMemo } from "react";
import { formatArrivalDayLong, todayLocal } from "@/lib/bookingDates";
import { formatCurrency } from "@/lib/money";
import { DeltaBadge, DualMeter } from "@/pages/reports/reportShared";
import type { DailyReport } from "@/types/camping";

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  if (names.length === 2) return `${names[0]} und ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} und ${names[names.length - 1]}`;
}

function lieVerb(names: string[]): string {
  if (names.length === 1 && names[0] === "Umsatz") return "liegt";
  return "liegen";
}

function istVerb(names: string[]): string {
  if (names.length === 1 && names[0] === "Umsatz") return "ist";
  return "sind";
}

function comparisonReadout(
  guestsDelta: number,
  arrivalsDelta: number,
  revenueDelta: number,
): string {
  const items = [
    { name: "Gäste", delta: guestsDelta },
    { name: "Anreisen", delta: arrivalsDelta },
    { name: "Umsatz", delta: revenueDelta },
  ];
  const up = items.filter((item) => item.delta > 0).map((item) => item.name);
  const down = items.filter((item) => item.delta < 0).map((item) => item.name);
  const flat = items.filter((item) => item.delta === 0).map((item) => item.name);
  if (up.length === 0 && down.length === 0) {
    return "Gäste, Anreisen und Umsatz entsprechen dem Vorjahr.";
  }
  const chunks: string[] = [];
  if (up.length > 0) {
    chunks.push(`${joinNames(up)} ${lieVerb(up)} über dem Vorjahr`);
  }
  if (down.length > 0) {
    chunks.push(`${joinNames(down)} ${lieVerb(down)} unter dem Vorjahr`);
  }
  if (flat.length > 0) {
    chunks.push(`${joinNames(flat)} ${istVerb(flat)} unverändert`);
  }
  return `${chunks.join(", ")}.`;
}

function CompareCard({
  label,
  detail,
  currentLabel,
  priorLabel,
  current,
  prior,
  formatValue,
}: {
  label: string;
  detail: string;
  currentLabel: string;
  priorLabel: string;
  current: number;
  prior: number | null;
  formatValue: (value: number) => string;
}) {
  return (
    <article className="rounded-xl border border-separator bg-surface p-4 shadow-sm sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-label">{label}</p>
          <p className="mt-0.5 text-xs text-label-tertiary">{detail}</p>
        </div>
        {prior != null ? <DeltaBadge current={current} prior={prior} /> : null}
      </div>
      <p className="mt-4 text-3xl font-semibold tracking-tight tabular-nums text-label">
        {formatValue(current)}
      </p>
      {prior != null ? (
        <DualMeter
          current={current}
          prior={prior}
          currentCaption={currentLabel}
          priorCaption={`${priorLabel}: ${formatValue(prior)}`}
        />
      ) : (
        <p className="mt-3 text-xs text-label-tertiary">Für diesen Tag gibt es keinen Vorjahreswert.</p>
      )}
    </article>
  );
}

export function DailyYearComparisonSection({
  report,
  currency,
}: {
  report: DailyReport;
  currency: string;
}) {
  const prior = report.prior_year_same_day;
  const isToday = report.date === todayLocal();
  const currentYear = new Date(`${report.date}T12:00:00`).getFullYear();

  const metrics = useMemo(() => {
    const invoicedCurrent = Number.parseFloat(report.invoiced_total) || 0;
    const invoicedPrior = prior ? Number.parseFloat(prior.invoiced_total) || 0 : null;
    return {
      invoicedCurrent,
      invoicedPrior,
      arrivalsCurrent: report.arrivals_count,
      arrivalsPrior: prior?.arrivals_count ?? null,
      guestsCurrent: report.arriving_guests_count,
      guestsPrior: prior?.arriving_guests_count ?? null,
    };
  }, [prior, report]);

  const readout =
    metrics.guestsPrior != null && metrics.arrivalsPrior != null && metrics.invoicedPrior != null
      ? comparisonReadout(
          metrics.guestsCurrent - metrics.guestsPrior,
          metrics.arrivalsCurrent - metrics.arrivalsPrior,
          metrics.invoicedCurrent - metrics.invoicedPrior,
        )
      : null;

  const money = (value: number) => formatCurrency(value, currency);
  const count = (value: number) => value.toLocaleString("de-DE");
  const priorYearLabel = prior ? String(prior.year) : "Vorjahr";

  return (
    <section className="space-y-4" aria-label="Vergleich zum Vorjahr">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
          {isToday ? "Tagesaktuell" : "Stichtag"}
        </p>
        <h2 className="mt-1 text-lg font-semibold text-label">
          {isToday ? "Heute im Vergleich zum Vorjahr" : `Vergleich am ${formatArrivalDayLong(report.date)}`}
        </h2>
        <p className="mt-1 text-sm text-label-secondary">
          {prior
            ? isToday
              ? `${formatArrivalDayLong(report.date)} gegen ${formatArrivalDayLong(prior.date)}. Gäste sind die Personen auf den Anreisen, der Umsatz die ausgestellten Rechnungen.`
              : `Gegen ${formatArrivalDayLong(prior.date)}.`
            : "Gäste, Anreisen und Umsatz für diesen Tag."}
        </p>
        {readout ? <p className="mt-2 text-sm font-medium text-label">{readout}</p> : null}
      </div>

      <div className="grid gap-3 lg:grid-cols-3">
        <CompareCard
          label="Gäste"
          detail="Personen auf den Anreisen"
          currentLabel={String(currentYear)}
          priorLabel={priorYearLabel}
          current={metrics.guestsCurrent}
          prior={metrics.guestsPrior}
          formatValue={count}
        />
        <CompareCard
          label="Anreisen"
          detail="Buchungen mit Anreise an diesem Tag"
          currentLabel={String(currentYear)}
          priorLabel={priorYearLabel}
          current={metrics.arrivalsCurrent}
          prior={metrics.arrivalsPrior}
          formatValue={count}
        />
        <CompareCard
          label="Umsatz"
          detail="Ausgestellte Rechnungen"
          currentLabel={String(currentYear)}
          priorLabel={priorYearLabel}
          current={metrics.invoicedCurrent}
          prior={metrics.invoicedPrior}
          formatValue={money}
        />
      </div>
    </section>
  );
}
